import "../../../shared/styles/form.css";

/* eslint-disable react-hooks/exhaustive-deps */
import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  notification,
  Row,
  Select,
  Space,
  Typography,
} from "antd";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { RootState } from "../../../app/store";
import {
  createOrUpadateDriverAsync,
  getProviderDataByIdAsync,
} from "../../../features/driver/driverSlice";
import {
  PasswordRegexExpression,
  PasswordRequirementText,
} from "../../../shared/constants/Resources";
import { WebRoutes } from "../../../shared/constants/WebRoutes";
import { CountryDTO } from "../../../shared/dtos/countryDTO";
import ProviderAdminDTO from "../../../shared/dtos/providerAdminDTO";
import { capitalizeFirstLetter } from "../../../utils/helpers/capitalizeString";
import ContainerLayout from "../../common/containerLayout";
import {
  countriesList,
  getCanonicalCountryName,
  getCountryByName,
  getFlagEmojiFromIso2,
} from "./utilCountry";

const { Text } = Typography;

const CountryOptionLabel = ({ country }: { country: CountryDTO }) => {
  const [showImage, setShowImage] = useState(true);

  return (
    <Space>
      {showImage ? (
        <img
          src={`/images/flag/${String(country.code).toLowerCase()}.webp`}
          alt={`${String(country.country)} flag`}
          width={18}
          height={14}
          onError={() => setShowImage(false)}
        />
      ) : (
        <span>{getFlagEmojiFromIso2(String(country.code))}</span>
      )}
      {country.country}
    </Space>
  );
};

export const DriverForm = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { saving, loadingDriverInfo, currentProvider, error, isSuccess } =
    useAppSelector((state: RootState) => state.drivers);

  const [form] = Form.useForm();

  let { driverId } = useParams();

  const countryOptions = useMemo(
    () =>
      countriesList.map((country) => ({
        value: String(country.country),
        label: <CountryOptionLabel country={country} />,
        searchText: `${String(country.country)} ${String(country.code)} ${String(
          country.phoneCode,
        )}`.toLowerCase(),
      })),
    [],
  );

  useEffect(() => {
    if (driverId) {
      dispatch(getProviderDataByIdAsync(+driverId));
    }
  }, []);

  useEffect(() => {
    if (currentProvider) {
      const { dni, firstName, lastName, email, mobile, profile, bank } =
        currentProvider;
      const canonicalCountryName = getCanonicalCountryName(profile?.country);
      let phone = mobile;
      const phoneCode = getCountryByName(canonicalCountryName)?.phoneCode;
      if (phoneCode) {
        const reg = new RegExp("^" + phoneCode.replace("+", "") + "");
        phone = phone?.replace(reg, "");
      }

      form.setFieldsValue({
        firstName,
        lastName,
        email,
        phone: Number(phone),
        dni,
        country: canonicalCountryName || profile?.country,
        city: profile?.city,
        accountHolder: bank?.ownerName,
        bank: bank?.bank,
        accountType: bank?.accountType,
        accountNumber: bank?.number,
        address: bank?.address,
        birthdate: bank?.dateBirth
          ? bank?.dateBirth === "0000-00-00"
            ? null
            : dayjs(bank.dateBirth, "YYYY-MM-DD")
          : null,
        password: "",
      });
    }
  }, [currentProvider, form]);

  const onFinish = async (values: any) => {
    const {
      firstName,
      lastName,
      email,
      phone,
      accountHolder,
      birthdate,
      country,
    } = values;
    let mobile = phone;
    const phoneCode = getCountryByName(country)?.phoneCode;
    if (phoneCode) {
      const code = phoneCode.replace("+", "");
      mobile = `${code}${phone}`;
    }

    const data: ProviderAdminDTO = {
      ...values,
      id: driverId ? +driverId : undefined,
      firstName: capitalizeFirstLetter(firstName),
      lastName: capitalizeFirstLetter(lastName),
      email: (email as string).toLowerCase().trim(),
      mobile,
      accountHolder: capitalizeFirstLetter(accountHolder),
      birthdate: birthdate ? (birthdate as Dayjs).format("YYYY-MM-DD") : null,
    };

    await dispatch(createOrUpadateDriverAsync(data));
  };

  useEffect(() => {
    if (isSuccess) {
      notification.success({
        message: `Conductor ${
          driverId ? "actualizado" : "creado"
        } correctamente!`,
        placement: "bottom",
        key: "global",
      });

      navigate(WebRoutes.DriverRoute, {
        replace: true,
      });
    }
  }, [isSuccess, navigate]);

  useEffect(() => {
    if (error && currentProvider) {
      notification.error({
        message: error,
        placement: "bottom",
        key: "global",
      });
    }
  }, [error]);

  return (
    <ContainerLayout
      title={!!!driverId ? "Crear nuevo conductor" : "Editar conductor"}
      showNotification={false}
    >
      <Col span={24}>
        <Form
          form={form}
          name="basic"
          onFinish={onFinish}
          autoComplete="off"
          layout="vertical"
        >
          <Row gutter={24}>
            <Col span={8}>
              <Text strong>Datos básicos</Text>

              <Form.Item
                label="DNI"
                name="dni"
                rules={[
                  { required: true, message: "Por favor ingresa un DNI!" },
                ]}
              >
                <Input />
              </Form.Item>

              <Form.Item
                label="Nombres"
                name="firstName"
                rules={[
                  { required: true, message: "Por favor ingresa un nombre!" },
                ]}
              >
                <Input />
              </Form.Item>

              <Form.Item
                label="Apellidos"
                name="lastName"
                rules={[
                  { required: true, message: "Por favor ingresa un apellido!" },
                ]}
              >
                <Input />
              </Form.Item>

              <Form.Item
                label="País"
                name="country"
                rules={[
                  { required: true, message: "Por favor selecciona un País!" },
                ]}
              >
                <Select
                  showSearch
                  placeholder="Seleccione el país."
                  onChange={(value: string) =>
                    form.setFieldValue("country", value)
                  }
                  filterOption={(inputValue, option) =>
                    String(option?.searchText || "").includes(
                      inputValue.toLowerCase(),
                    )
                  }
                  options={countryOptions}
                />
              </Form.Item>

              <Form.Item
                label="Ciudad"
                name="city"
                rules={[
                  { required: true, message: "Por favor ingresa una ciudad!" },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Text strong>Cuenta de perfil</Text>
              <Form.Item
                label="Correo electrónico"
                name="email"
                rules={[
                  { required: true, message: "Por favor ingresa un correo!" },
                  { type: "email", message: "Ingrese un correo válido!" },
                ]}
              >
                <Input />
              </Form.Item>

              <Form.Item
                label="Contraseña"
                name="password"
                rules={[
                  {
                    required: !!!driverId,
                    message: "Por favor ingresa una clave!",
                  },
                  {
                    pattern: PasswordRegexExpression,
                    message: PasswordRequirementText,
                  },
                ]}
                hasFeedback
              >
                <Input.Password />
              </Form.Item>

              <Form.Item
                name="confirm"
                label="Confirmar contraseña"
                dependencies={["password"]}
                hasFeedback
                rules={[
                  {
                    required: !!!driverId,
                    message: "Por favor confirma la clave!",
                  },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue("password") === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        new Error("Las contraseñas no coinciden!"),
                      );
                    },
                  }),
                ]}
              >
                <Input.Password />
              </Form.Item>

              <Form.Item
                shouldUpdate={(prevValues, curValues) =>
                  prevValues.country !== curValues.country
                }
              >
                {({ getFieldValue }) => {
                  return (
                    <Form.Item
                      label="Teléfono"
                      name="phone"
                      rules={[
                        {
                          required: true,
                          message: "Por favor ingresa un teléfono!",
                        },
                        { type: "integer" },
                      ]}
                    >
                      <InputNumber
                        style={{ width: "100%" }}
                        prefix={
                          getCountryByName(getFieldValue("country"))?.phoneCode ||
                          ""
                        }
                      />
                    </Form.Item>
                  );
                }}
              </Form.Item>
            </Col>

            <Col span={8}>
              <Text strong>Cuenta bancaria</Text>

              <Form.Item
                label="Nombre del titular de la cuenta"
                name="accountHolder"
                rules={[
                  { required: true, message: "Por favor ingresa un nombre!" },
                ]}
              >
                <Input />
              </Form.Item>

              <Form.Item
                label="Nombre del banco"
                name="bank"
                rules={[
                  {
                    required: true,
                    message: "Por favor ingresa el nombre del banco!",
                  },
                ]}
              >
                <Input />
              </Form.Item>

              <Form.Item
                label="Tipo de cuenta"
                name="accountType"
                rules={[
                  {
                    required: true,
                    message: "Por favor ingresa un tipo de cuenta",
                  },
                ]}
              >
                <Input />
              </Form.Item>

              <Form.Item
                label="Número de la cuenta bancaria"
                name="accountNumber"
                rules={[
                  {
                    required: true,
                    message: "Por favor ingresa un número de cuenta!",
                  },
                ]}
              >
                <InputNumber style={{ width: "100%" }} />
              </Form.Item>

              <Form.Item
                label="Dirección"
                name="address"
                rules={[
                  {
                    required: true,
                    message: "Por favor ingresa una dirección!",
                  },
                ]}
              >
                <Input />
              </Form.Item>

              <Form.Item
                label="Fecha de nacimiento"
                name="birthdate"
                rules={[
                  { required: true, message: "Por favor ingresa una fecha!" },
                ]}
              >
                <DatePicker
                  format="YYYY-MM-DD"
                  style={{ width: "100%" }}
                  placeholder=""
                />
              </Form.Item>
            </Col>
          </Row>

          <Space direction="horizontal">
            <Button
              type="primary"
              htmlType="submit"
              disabled={loadingDriverInfo}
              loading={saving}
            >
              Guardar
            </Button>
            <Button
              onClick={() =>
                navigate(WebRoutes.DriverRoute, {
                  replace: true,
                })
              }
            >
              Cancelar
            </Button>
          </Space>
        </Form>
      </Col>
    </ContainerLayout>
  );
};

export default DriverForm;
